import { useState } from "react";
import { esc } from "../utils/format.js";
import { state } from "../state/workspaceStore.js";
import { getWorkspaceSession } from "../utils/domain/workspace.js";

const API_URL = "https://api.praachinorganics.com/api/Auth";

function saveAdminSession(admin) {
  const token = admin?.token ?? admin?.Token;
  const id = admin?.id ?? admin?.Id;
  const name = admin?.name ?? admin?.Name ?? "";
  const email = admin?.email ?? admin?.Email;
  const role = admin?.role ?? admin?.Role;
  const expiresAt =
    admin?.expiresAtUtc ??
    admin?.ExpiresAtUtc ??
    admin?.expiresAt ??
    admin?.ExpiresAt;

  if (
    !token ||
    id == null ||
    !email ||
    String(role).toLowerCase() !== "admin"
  ) {
    throw new Error(
      "The server did not return a valid admin token, ID, email, and role.",
    );
  }

  if (
    expiresAt &&
    (!Number.isFinite(Date.parse(expiresAt)) ||
      Date.parse(expiresAt) <= Date.now())
  ) {
    throw new Error("The login token has expired. Please try again.");
  }

  sessionStorage.setItem("adminToken", token);
  sessionStorage.setItem("adminId", String(id));
  sessionStorage.setItem("adminName", name);
  sessionStorage.setItem("adminEmail", email);
  sessionStorage.setItem("adminRole", String(role));

  if (expiresAt) {
    sessionStorage.setItem("adminExpiresAtUtc", expiresAt);
  } else {
    sessionStorage.removeItem("adminExpiresAtUtc");
  }
}

export function clearAdminSession() {
  [
    "adminToken",
    "adminId",
    "adminName",
    "adminEmail",
    "adminRole",
    "adminExpiresAtUtc",
  ].forEach((key) => sessionStorage.removeItem(key));
}

export function getAdminSession() {
  const token = sessionStorage.getItem("adminToken");
  const role = sessionStorage.getItem("adminRole");
  const expiresAt = sessionStorage.getItem("adminExpiresAtUtc");

  if (
    !token ||
    role?.toLowerCase() !== "admin" ||
    (expiresAt &&
      (!Number.isFinite(Date.parse(expiresAt)) ||
        Date.parse(expiresAt) <= Date.now()))
  ) {
    clearAdminSession();
    return null;
  }

  return {
    id: sessionStorage.getItem("adminId"),
    name: sessionStorage.getItem("adminName"),
    email: sessionStorage.getItem("adminEmail"),
    role,
    token,
    expiresAtUtc: expiresAt,
  };
}

async function loginAdmin(email, password) {
  const url = `${API_URL}/admin/login`;

  console.log("[Admin Login] Sending request:", url);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email.trim(),
      password,
    }),
  });

  const result = await response.json().catch(() => null);

  console.log("[Admin Login] HTTP status:", response.status);

  if (!response.ok) {
    throw new Error(
      result?.message ??
      result?.Message ??
      `Login failed with HTTP ${response.status}.`,
    );
  }

  if (result?.success === false || result?.Success === false) {
    throw new Error(
      result?.message ?? result?.Message ?? "Invalid email or password.",
    );
  }

  const admin = result?.data ?? result?.Data;

  if (!admin) {
    throw new Error("The server response does not contain admin data.");
  }

  saveAdminSession(admin);
  return admin;
}

export async function logoutAdmin() {
  const token = sessionStorage.getItem("adminToken");

  try {
    if (token) {
      await fetch(`${API_URL}/admin/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
  } catch (error) {
    console.error("[Admin Logout]", error);
  } finally {
    clearAdminSession();
    window.location.hash = "login";
    window.location.reload();
  }
}

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  async function handleLogin(event) {
    event.preventDefault();

    console.log("[Admin Login] Form submitted");

    if (loading) return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "");

    setError("");

    if (!email || !password) {
      setError("Please enter your email address and password.");
      return;
    }

    setLoading(true);

    try {
      await loginAdmin(email, password);

      console.log("[Admin Login] Authentication successful");

      const adminSession = getWorkspaceSession(localStorage, sessionStorage);

      if (!adminSession) {
        throw new Error("LOGIN_FAILED");
      }

      state.activeAccount = adminSession;
      state.signedIn = true;
      state.authMessage = "";
      state.current = {
        route: "dashboard",
        id: null,
      };
      state.dialog = null;

      window.location.hash = "dashboard";
      state.refresh();
    } catch (err) {
      // Keep technical details in the console for development.
      console.error("[Admin Login] Technical error:", err);

      const message = String(err?.message ?? "").toLowerCase();

      // Use the same message for invalid email and password.
      if (
        message.includes("invalid email") ||
        message.includes("invalid password") ||
        message.includes("invalid credentials") ||
        message.includes("unauthorized") ||
        message.includes("http 401") ||
        message.includes("http 403")
      ) {
        setError(
          "Invalid email or password. Please check your credentials and try again.",
        );
      } else {
        setError(
          "We couldn’t sign you in at this time. Please try again later.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <aside className="auth-art">
        <div className="auth-logo">
          <img src="/assets/praachin-logo-white.png" alt="Praachin Organics" />
        </div>

        <div className="auth-story">
          <span className="eyebrow">ADMIN WORKSPACE</span>

          <h1>
            Good work.
            <br />
            Thoughtfully
            <br />
            organised.
          </h1>

          <p>
            A calm place for your catalogue, orders and everyday operations.
          </p>

          <div className="auth-features">
            <span>Catalogue &amp; stock</span>
            <span>Orders &amp; collections</span>
          </div>
        </div>

        <small>{esc(state.db.profile.company)}</small>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          <div className="auth-mobile-logo">
            <img
              src="/assets/praachin-logo-white.png"
              alt="Praachin Organics"
            />
          </div>

          <div className="eyebrow">ADMIN PORTAL</div>

          <h2>Welcome back</h2>

          <p className="auth-intro">
            Open your workspace and continue where you left off.
          </p>

          {state.authMessage ? (
            <p className="auth-success" role="status">
              {esc(state.authMessage)}
            </p>
          ) : null}

          <form id="login-form" noValidate onSubmit={handleLogin}>
            <label className="field auth-field" htmlFor="login-email">
              <span>Email address</span>

              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder="Enter your admin email"
                aria-describedby="access-error"
                required
                disabled={loading}
              />
            </label>

            <label className="field auth-field" htmlFor="login-password">
              <span>Password</span>
            </label>

            <div className="password-control">
              <input
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                maxLength={128}
                aria-describedby="access-error"
                required
                disabled={loading}
              />

              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                disabled={loading}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <p
              id="access-error"
              className="error"
              role="alert"
              aria-live="polite"
              tabIndex={-1}
            >
              {error}
            </p>

            <button
              className="btn auth-submit"
              type="submit"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Login"}
            </button>
          </form>

          <p className="auth-footnote">
            Authorized administrator access. Please keep your login credentials
            secure.
          </p>
        </div>
      </main>
    </div>
  );
}
